import { type ActionReducerMapBuilder, isAnyOf } from '@reduxjs/toolkit';
import omit from 'lodash/omit';
import type { RecommendedItem } from 'src/components/Shared/RecommendedToolsMaterials/types';
import { filterIssueMedia } from 'src/helper/utils';
import { splitRecommendedItems } from 'src/redux/issues/reducers';
import {
  addIssueFromServiceRequest,
  addIssueRecommendedItem,
  addIssueRecommendedItemFromWorkOrder,
  approveIssue,
  createIssueFromWorkOrder,
  deleteIssueRecommendedItem,
  deleteIssueRecommendedItemFromWorkOrder,
  editIssueFromServiceRequest,
  getTripDetails,
  unassignIssueFromServiceRequest,
  unassignIssueFromWorkOrder,
  updateIssueDataFromWorkOrder,
  updateIssueRecommendedItem,
  updateIssueRecommendedItemFromWorkOrder,
} from 'src/redux/issues/api';
import type { InitialIssueStateType } from 'src/redux/issues/types';

const extraReducers = (builder: ActionReducerMapBuilder<InitialIssueStateType>) => {
  // fetch issue trip details api
  builder.addCase(getTripDetails().fulfilled, (state: InitialIssueStateType, action) => {
    const trip = action.payload;
    if (!trip?.trip_id && !trip.issueId) return;
    state.issuesSource[trip.issueId] = {
      ...(state?.issuesSource?.[trip?.issueId] || {}),
      tripSource: {
        ...(state?.issuesSource?.[trip?.issueId]?.tripSource || {}),
        [trip?.trip_id]: {
          ...(state?.issuesSource?.[trip?.issueId]?.tripSource?.[trip?.trip_id] || {}),
          ...omit(trip, ['issueId']),
        },
      },
    };
  });

  builder.addMatcher(
    isAnyOf(addIssueRecommendedItem().fulfilled, addIssueRecommendedItemFromWorkOrder().fulfilled),
    (state: InitialIssueStateType, action) => {
      const { issueId, issue_recommended_item_id, item_type, name, source } = action.payload;
      const issue = state.issuesSource[issueId];
      if (!issue) return;
      const arrayKey = item_type === 'tool' ? 'recommended_tools' : 'recommended_materials';
      const tempIndex = (issue[arrayKey] || []).findIndex(
        (item) => item.tempKey && item.name === name && !item.issue_recommended_item_id,
      );
      if (tempIndex >= 0) {
        // Promote the optimistic item to real — keep tempKey so the React key stays stable
        issue[arrayKey] = (issue[arrayKey] || []).map((item, index) =>
          index === tempIndex ? { ...item, issue_recommended_item_id, source } : item,
        );
      } else {
        issue[arrayKey] = [
          ...(issue[arrayKey] || []),
          { issue_recommended_item_id, item_type, name, source },
        ];
      }
    },
  );

  builder.addMatcher(
    isAnyOf(
      updateIssueRecommendedItem().fulfilled,
      updateIssueRecommendedItemFromWorkOrder().fulfilled,
    ),
    (state: InitialIssueStateType, action) => {
      const { issueId, issue_recommended_item_id, item_type, name, source } = action.payload;
      const issue = state.issuesSource[issueId];
      if (!issue) return;
      const updatedItem = { issue_recommended_item_id, item_type, name, source };
      const arrayKey = item_type === 'tool' ? 'recommended_tools' : 'recommended_materials';
      issue[arrayKey] = (issue[arrayKey] || []).map((item) =>
        item.issue_recommended_item_id === issue_recommended_item_id ? updatedItem : item,
      );
    },
  );

  builder.addMatcher(
    isAnyOf(
      deleteIssueRecommendedItem().fulfilled,
      deleteIssueRecommendedItemFromWorkOrder().fulfilled,
    ),
    (state: InitialIssueStateType, action) => {
      const { issueId, issue_recommended_item_id, item_type } = action.payload;
      const issue = state.issuesSource[issueId];
      if (!issue) return;
      const arrayKey = item_type === 'tool' ? 'recommended_tools' : 'recommended_materials';
      issue[arrayKey] = (issue[arrayKey] || []).filter(
        (item) => item.issue_recommended_item_id !== issue_recommended_item_id,
      );
    },
  );

  builder.addMatcher(
    isAnyOf(addIssueFromServiceRequest().pending, createIssueFromWorkOrder().pending),
    (state: InitialIssueStateType) => {
      state.isAddingOrEditingIssue = true;
    },
  );

  builder.addMatcher(
    isAnyOf(addIssueFromServiceRequest().fulfilled, createIssueFromWorkOrder().fulfilled),
    (state: InitialIssueStateType, action) => {
      const { issue_recommended_items: rawItems, ...restPayload } = action.payload;
      const issue_recommended_items = rawItems as RecommendedItem[] | undefined;
      state.issuesSource = {
        ...state.issuesSource,
        [restPayload?.issue_id]: {
          ...restPayload,
          media: filterIssueMedia(restPayload?.media),
          ...(issue_recommended_items && splitRecommendedItems(issue_recommended_items)),
        },
      };
      state.isAddingOrEditingIssue = false;
    },
  );
  builder.addMatcher(
    isAnyOf(addIssueFromServiceRequest().rejected, createIssueFromWorkOrder().rejected),
    (state: InitialIssueStateType) => {
      state.isAddingOrEditingIssue = false;
    },
  );

  builder.addMatcher(
    isAnyOf(
      editIssueFromServiceRequest().pending,
      updateIssueDataFromWorkOrder().pending,
      approveIssue().pending,
    ),
    (state: InitialIssueStateType) => {
      state.isAddingOrEditingIssue = true;
    },
  );
  builder.addMatcher(
    isAnyOf(
      editIssueFromServiceRequest().fulfilled,
      updateIssueDataFromWorkOrder().fulfilled,
      approveIssue().fulfilled,
    ),
    (state: InitialIssueStateType, action) => {
      const { issue_recommended_items: rawItems, ...restPayload } = action.payload;
      const issue_recommended_items = rawItems as RecommendedItem[] | undefined;
      state.issuesSource = {
        ...state.issuesSource,
        [restPayload?.issue_id]: {
          ...state?.issuesSource?.[restPayload?.issue_id],
          ...restPayload,
          media: filterIssueMedia(restPayload?.media),
          ...(issue_recommended_items && splitRecommendedItems(issue_recommended_items)),
        },
      };
      state.isAddingOrEditingIssue = false;
    },
  );
  builder.addMatcher(
    isAnyOf(
      editIssueFromServiceRequest().rejected,
      updateIssueDataFromWorkOrder().rejected,
      approveIssue().rejected,
    ),
    (state: InitialIssueStateType) => {
      state.isAddingOrEditingIssue = false;
    },
  );

  // unAssign Issue
  builder.addMatcher(
    isAnyOf(unassignIssueFromServiceRequest().pending, unassignIssueFromWorkOrder().pending),
    (state: InitialIssueStateType) => {
      state.isIssueBeingUnassigned = true;
    },
  );
  builder.addMatcher(
    isAnyOf(unassignIssueFromServiceRequest().fulfilled, unassignIssueFromWorkOrder().fulfilled),
    (state: InitialIssueStateType) => {
      state.isIssueBeingUnassigned = false;
    },
  );
  builder.addMatcher(
    isAnyOf(unassignIssueFromServiceRequest().rejected, unassignIssueFromWorkOrder().rejected),
    (state: InitialIssueStateType) => {
      state.isIssueBeingUnassigned = false;
    },
  );
};
export default extraReducers;
